<template>
  <!-- Sem visual próprio: o estado é publicado em useSyncStatus e exibido pelo card único do header (StatusSyncPortais) -->
  <span hidden></span>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import * as signalR from '@microsoft/signalr'
import eventBus from '@/views/eventBus.js'
import { showToast, isJSON } from '@/mixins/fcgeral'
import { getCnpjAtivo } from '@/mixins/empresaAtiva'
import { useConfig } from '@/composables/useConfig.js'
import { useStore } from 'vuex'
import { usePublicarSync, calcularEstadoSync } from '@/composables/useSyncStatus.js'

const baseUrl = process.env.VUE_APP_BASE_URL

const props = defineProps({
  idPortal: { type: Number, default: 8 },
})

const portalLabel = computed(() => {
  if (props.idPortal === 5) return 'BLL'
  if (props.idPortal === 8) return 'BNC'
  if (props.idPortal === 7) return 'LicitaNET'
  return `Portal ${props.idPortal}`
})

const { configGeral, reloadConfig } = useConfig()
const store = useStore()

const cnpj = ref('00000000000000')
const running = ref(false)
const statusMsg = ref('')
const countdownSeconds = ref(0)
const timeSyncConfig = ref(null)
const timeUntilSync = ref(null)
// Informações p/ o card de status do header
const erroMsg = ref('')
const erroEm = ref(null)
const ultimaEm = ref(null)
const ultimoResultado = ref('')
const concluido = ref(false)
// Aviso discreto (não é falha): já existe uma busca igual em andamento, ex.: outra aba/navegador com o mesmo CNPJ
const avisoMsg = ref('')

let connection = null
let isSyncing = false
let countdownTimer = null
let configInterval = null
let timeUntilSyncInterval = null
let removeStartSyncListener = null
// Fallback de polling: backstop caso o push SignalR (ReportProgress 100) se perca
let statusPoller = null
let statusJobId = null

function stopStatusPoller() {
  if (statusPoller) { clearInterval(statusPoller); statusPoller = null }
  statusJobId = null
}

// Consulta o status do job direto no banco (via API). Libera o badge mesmo se o
// SignalR não entregar o 100 (ex.: job processado pelo worker da VPS na fila compartilhada).
async function pollJobStatus() {
  if (!statusJobId) return
  try {
    let token = ''
    try { token = JSON.parse(localStorage.getItem('userDados') || '{}').token || '' } catch {}
    const resp = await fetch(`${baseUrl}/api/PregoesSyncWS/job/${statusJobId}`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include'
    })
    if (!resp.ok) return
    const job = await resp.json()
    if (!job || !job.status) return
    if (job.status === 'done') { stopStatusPoller(); if (isSyncing) finalizeSync() }
    else if (job.status === 'failed') { stopStatusPoller(); if (isSyncing) handleFailure(job.errorMsg || 'Sincronização falhou') }
  } catch { /* silencioso — tenta de novo no próximo tick */ }
}

const formattedCountdown = computed(() => {
  const h = Math.floor(countdownSeconds.value / 3600)
  const m = Math.floor((countdownSeconds.value % 3600) / 60)
  const s = countdownSeconds.value % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
})

const timeUntilSyncMessage = computed(() => {
  if (timeUntilSync.value === null) return null
  const h = Math.floor(timeUntilSync.value / 3600)
  const m = Math.floor((timeUntilSync.value % 3600) / 60)
  const s = timeUntilSync.value % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
})

const isWithinTimeSync = computed(() => {
  const ts = timeSyncConfig.value?.timeSync
  if (!ts) return false
  const [startStr, endStr] = ts.split('|')
  const now = new Date()
  const [sh, sm] = startStr.split(':').map(Number)
  const [eh, em] = endStr.split(':').map(Number)
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), sh, sm, 0)
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), eh, em, 0)
  if (end < start) end.setDate(end.getDate() + 1)
  return now >= start && now <= end
})

function pauseCountdown() {
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null }
}

function startCountdown() {
  pauseCountdown()
  countdownTimer = setInterval(() => {
    if (countdownSeconds.value > 0) {
      countdownSeconds.value--
    } else {
      pauseCountdown()
      startSync()
    }
  }, 1000)
}

function resetCountdown() {
  pauseCountdown()
  countdownSeconds.value = timeSyncConfig.value?.timeUpAlerta ?? 300
  running.value = false
  concluido.value = false
  if (isWithinTimeSync.value && !isSyncing) startCountdown()
}

function calculateTimeUntilSync() {
  const ts = timeSyncConfig.value?.timeSync
  if (!ts) { timeUntilSync.value = null; return }
  const [startStr] = ts.split('|')
  const now = new Date()
  const [h, m] = startStr.split(':').map(Number)
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0)
  if (now > next) next.setDate(next.getDate() + 1)
  timeUntilSync.value = Math.floor((next - now) / 1000)
}

function refreshSyncConfig() {
  calculateTimeUntilSync()
  pauseCountdown()
  if (isWithinTimeSync.value && !running.value && !isSyncing) startCountdown()
}

function handleFailure(msg) {
  stopStatusPoller()
  console.error(msg)
  statusMsg.value = `Erro: ${msg}`
  erroMsg.value = String(msg)
  erroEm.value = Date.now()
  isSyncing = false
  running.value = false
  if (connection) { connection.stop().catch(() => {}); connection = null }
  refreshSyncConfig()
  resetCountdown()
}

// A API recusou porque já existe uma busca igual em andamento (outra aba, outro navegador ou outro dispositivo
// com o mesmo CNPJ). Não é falha: só avisa com discrição e tenta de novo na próxima execução.
function handleJaEmAndamento(manual) {
  stopStatusPoller()
  isSyncing = false
  running.value = false
  erroMsg.value = ''
  avisoMsg.value = 'Já há uma busca em andamento (talvez em outra aba ou dispositivo). Tentando de novo na próxima execução.'
  if (manual) showToast('info', avisoMsg.value, 'bottom-end', 5000)
  if (connection) { connection.stop().catch(() => {}); connection = null }
  refreshSyncConfig()
  resetCountdown()
}

async function finalizeSync() {
  stopStatusPoller()
  isSyncing = false
  let bReloadAlerta = false
  let displayMsg = 'Concluído'

  if (isJSON(statusMsg.value)) {
    const data = JSON.parse(statusMsg.value)
    store.commit('changeSyncDados', data)
    displayMsg = data.msg || `Alertas ${portalLabel.value}: ${data.qntAlerta ?? 0}`
    if ((data.qntAlerta || 0) > 0) {
      bReloadAlerta = true
      showToast('success', data.msg || `Novos alertas ${portalLabel.value}`, 'bottom-end', 6000)
      eventBus.emit('play-alert', data.msg)
    }
  }

  eventBus.emit('syncFinishedAlertaBNC', bReloadAlerta)

  // Mostra resultado no badge por 5 segundos
  statusMsg.value = displayMsg
  running.value = true
  concluido.value = true
  erroMsg.value = ''
  avisoMsg.value = ''
  ultimaEm.value = Date.now()
  ultimoResultado.value = displayMsg

  if (connection) {
    try { await connection.stop() } catch (e) { console.error(e) } finally { connection = null }
  }

  setTimeout(() => {
    refreshSyncConfig()
    resetCountdown()
  }, 5000)
}

async function startSync() {
  if (isSyncing) { showToast('error', `Sincronização ${portalLabel.value} já em andamento.`); return }

  if (connection) {
    connection.off('ReportProgress')
    try { await connection.stop() } catch {}
    connection = null
  }

  isSyncing = true
  pauseCountdown()
  running.value = true
  concluido.value = false
  erroMsg.value = ''
  avisoMsg.value = ''
  statusMsg.value = 'Enfileirando...'

  let tokenAuth = ''
  try {
    const userData = JSON.parse(localStorage.getItem('userDados') || '{}')
    tokenAuth = userData.token || ''
    // Multi-CNPJ: grupo SignalR e sync no contexto da empresa ativa
    cnpj.value = getCnpjAtivo() || userData.cnpj || cnpj.value
  } catch (e) { console.error(e) }

  const groupName = `Sync_BNC_${props.idPortal}_${cnpj.value}`

  connection = new signalR.HubConnectionBuilder()
    .withUrl(`${baseUrl}/progressHub`, {
      skipNegotiation: true,
      accessTokenFactory: () => tokenAuth,
      transport: signalR.HttpTransportType.WebSockets
    })
    .withAutomaticReconnect()
    .build()

  connection.onreconnecting(() => handleFailure('Reconexão falhou'))

  connection.on('ReportProgress', ([pct, msg]) => {
    try {
      statusMsg.value = (typeof msg === 'string') ? msg : JSON.stringify(msg)
      if (pct >= 100) finalizeSync()
      else if (pct < 0) handleFailure(statusMsg.value)
    } catch (e) { handleFailure(e.message) }
  })

  try {
    await connection.start()
    await connection.invoke('JoinGroup', groupName)
    statusMsg.value = 'Aguardando Workers...'

    const response = await fetch(`${baseUrl}/api/PregoesSyncWS/startAlertaBNC?idPortal=${props.idPortal}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        groupName,
        Authorization: `Bearer ${tokenAuth}`
      },
      credentials: 'include',
      body: '{}'
    })

    if (!response.ok && response.status !== 202) {
      // a API responde { error } — mostra a mensagem real em vez de só "HTTP 400"
      const corpo = await response.json().catch(() => null)
      if (response.status === 400 && /em andamento/i.test(corpo?.error || '')) { handleJaEmAndamento(false); return }
      throw new Error(corpo?.error || `HTTP ${response.status}`)
    }

    // Inicia o fallback de polling com o jobId retornado
    const startData = await response.json().catch(() => null)
    if (startData?.jobId) {
      stopStatusPoller()
      statusJobId = startData.jobId
      statusPoller = setInterval(pollJobStatus, 15000)
    }
  } catch (err) {
    handleFailure(err.message || err)
  }
}

const estado = computed(() => calcularEstadoSync({
  erroMsg: erroMsg.value, running: running.value, concluido: concluido.value, dentroJanela: isWithinTimeSync.value
}))

usePublicarSync(`alertas-${props.idPortal}`, () => {
  const e = estado.value
  let detalhe = ''
  let segundos = null
  let rotuloSegundos = ''
  if (e === 'erro') detalhe = erroMsg.value
  else if (e === 'rodando') detalhe = statusMsg.value || 'Processando...'
  else if (running.value) detalhe = statusMsg.value // resultado da execução que acabou de terminar
  else if (e === 'ocioso') { if (countdownSeconds.value > 0) { segundos = countdownSeconds.value; rotuloSegundos = 'Próxima em' } }
  else if (timeUntilSync.value !== null) { segundos = timeUntilSync.value; rotuloSegundos = 'Automático inicia em' }
  return {
    label: `Alertas ${portalLabel.value}`, ordem: ({ 5: 3, 8: 4, 7: 5 })[props.idPortal] ?? 9, estado: e, detalhe: detalhe || avisoMsg.value, segundos, rotuloSegundos,
    ultimaEm: ultimaEm.value, ultimoResultado: ultimoResultado.value, erroEm: erroEm.value
  }
})

onMounted(() => {
  reloadConfig()
  timeSyncConfig.value = configGeral.value
  resetCountdown()

  const handler = () => startSync()
  eventBus.on('startSyncAlertaBNC', handler)
  removeStartSyncListener = () => eventBus.off('startSyncAlertaBNC', handler)

  refreshSyncConfig()
  configInterval = setInterval(refreshSyncConfig, 30000)
  timeUntilSyncInterval = setInterval(calculateTimeUntilSync, 1000)
})

onBeforeUnmount(() => {
  if (removeStartSyncListener) removeStartSyncListener()
  clearInterval(configInterval)
  clearInterval(timeUntilSyncInterval)
  pauseCountdown()
  stopStatusPoller()
  if (connection) connection.stop().catch(() => {})
})
</script>

